'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export default function PrivacyPolicyPage() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Privacy Policy</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Learn how we protect and manage your data
            </motion.p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300 mb-4">Effective Date: 01 November, 2024</p>
              <p className="text-slate-300">At 1 Dollar Cloud, a service provided by Global Ads Media, LLC., we are committed to safeguarding your privacy and ensuring a secure and positive experience while using our hosting services. This Privacy Policy outlines how we collect, use, and protect your personal information.</p>
            </div>

            {[
              {
                title: "1. Information We Collect",
                content: [
                  "We collect several types of information to provide, improve, and secure our services:",
                  "Personal Information: When you sign up for our hosting services, you may be required to provide personal information, including but not limited to your name, email address, phone number, and, in some cases, a government-issued ID card for identity verification purposes.",
                  "Account and Payment Information: When you purchase hosting services, we collect your credit or debit card details and billing information to process payments. This information is securely stored and processed through trusted payment gateways.",
                  "Login Information: You can sign up for our services using your email address or third-party credentials such as Google, Facebook, or Twitter. In such cases, we may collect and store your profile data from these third-party accounts to facilitate login and provide a seamless user experience.",
                  "Data Stored on Our Servers: As part of our hosting services, you may store personal, business, or website-related data on our servers. This includes any content you upload, such as files, documents, emails, and other digital information."
                ]
              },
              {
                title: "2. How We Use Your Information",
                content: [
                  "We use the collected information for the following purposes:",
                  "To Provide and Manage Services: Your personal information is used to create and manage your account, process payments, and deliver the hosting services you requested.",
                  "To Communicate with You: We may use your contact information to send you service-related notifications, account updates, and important information about your hosting account. We may also send you promotional emails if you have opted in to receive them.",
                  "For Payment Processing: We use your payment information to process your purchases securely. All credit and debit card transactions are processed through third-party payment providers, and we do not store your full card details.",
                  "For Identity Verification: If required, we may use your ID card or other personal details to verify your identity for security purposes and prevent fraudulent activity.",
                  "To Improve Our Services: We may analyze user data to improve the functionality, security, and user experience of our hosting services."
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4">{paragraph}</p>
                ))}
              </motion.div>
            ))}

            {[
              {
                title: "3. How We Protect Your Information",
                content: "We prioritize the security of your personal information. We implement a range of security measures, including encryption, firewalls, and access controls, to protect your data from unauthorized access, alteration, disclosure, or destruction. Additionally, we only store payment information with third-party payment providers that comply with industry standards, such as the Payment Card Industry Data Security Standard (PCI DSS)."
              },
              {
                title: "4. Data Retention",
                content: "We retain your personal and business information as long as necessary to provide you with our services, comply with legal obligations, resolve disputes, and enforce our agreements. If you wish to delete your account or request that your data be erased, please contact us through our support channels."
              },
              {
                title: "5. Sharing Your Information",
                content: "We do not sell, rent, or share your personal information with third parties for marketing purposes without your consent. However, we may share your information with service providers and for legal compliance purposes."
              },
              {
                title: "6. Third-Party Links",
                content: "Our website or hosting services may contain links to third-party websites or services that are not owned or controlled by 1 Dollar Cloud or Global Ads Media, LLC. We are not responsible for the privacy practices of these third-party sites and encourage you to review their privacy policies before providing any personal information."
              },
              {
                title: "7. Your Rights and Choices",
                content: "You have the right to access, correct, and delete your personal information. You can also opt out of receiving marketing emails at any time."
              },
              {
                title: "8. Changes to This Privacy Policy",
                content: "We reserve the right to update or modify this Privacy Policy at any time. Any changes will be posted on this page, and we will update the 'Effective Date' at the top of the policy."
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                <p className="text-slate-300">{section.content}</p>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-800/50 rounded-xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">9. Contact Us</h2>
              <p className="text-slate-300 mb-4">If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
              <div className="text-slate-300">
                <p className="font-semibold">1 Dollar Cloud</p>
                <p>a service of Global Ads Media, LLC.</p>
                <p>66 West Flagler Street Miami Florida 33130 USA</p>
                <p className="mt-4">Email:</p>
                <p>info@1dollarcloud.com</p>
                <p>info@globaladsmedia.us</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}